import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { GridApiCommunity } from "@mui/x-data-grid/models/api/gridApiCommunity";
import { FC, ReactElement, useContext } from "react";
import { FieldValues, UseFormReturn, useFormContext } from "react-hook-form";
import { v4 } from "uuid";
import {
  validateInputForm,
  validateListViewRequiredRows,
} from "../model/field-validation";
import {
  Configuration,
  FieldConfiguration,
  FormPropertiesConfiguration,
  FormRowConfiguration,
  PPComponent,
  SectionConfiguration,
} from "../model/form-configuration";

import { red } from "@mui/material/colors";
import { cloneDeep, isEqual } from "lodash";
import {
  AppContextType,
  ListViewContext,
  ListViewContextType,
  ListViewRowModel,
  ListViewSubmitDataModel,
  ListViewsContextType,
  StateContext,
} from "../../context/ApplicationContext";
import {
  Field,
  Form,
  Section,
  Tuple,
  isTuple,
  isTupleArray,
  tupleArrayToStringArray,
} from "../model/form-submit";
import { funcExecutor } from "../../functions/generic-functions";

const columnPadding: number = 20;

const style: SxProps = {
  my: 0,
  padding: 2,
};

const getTextWidth = (
  text: string,
  font: string | undefined | null
): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  context!.font = font ?? getComputedStyle(document.body).font;

  return context!.measureText(text).width;
};

const isFieldConfiguration = (
  conf: FieldConfiguration | FormRowConfiguration | ReactElement | undefined
): conf is FieldConfiguration | undefined => {
  return conf !== undefined && (conf as FieldConfiguration).label !== undefined;
};

const isFormRowConfiguration = (
  conf: Configuration
): conf is FormRowConfiguration | undefined => {
  return (
    conf !== undefined && (conf as FormRowConfiguration).fields !== undefined
  );
};

const getGridHeaders = (
  fieldConf: Array<FieldConfiguration>
): FieldConfiguration[] => {
  return fieldConf.filter((field: FieldConfiguration) =>
    field.extendedAttributes ? field.extendedAttributes["isGridHeader"] : false
  );
};

const unwrapFieldConfiguration = (
  rowConfiguration: Array<Configuration>
): Array<FieldConfiguration> => {
  let fieldConfigs: Array<FieldConfiguration> = [] as FieldConfiguration[];
  rowConfiguration.forEach((conf: Configuration) => {
    if (conf && isFieldConfiguration(conf)) fieldConfigs.push(conf);
    if (conf && isFormRowConfiguration(conf)) {
      conf.fields.forEach((field: FieldConfiguration | ReactElement) => {
        if (isFieldConfiguration(field)) {
          fieldConfigs.push(field);
        }
      });
    }
  });

  return fieldConfigs;
};

const customPagination = (props: any) => {
  return <span />;
};

const PPViewSection: FC<{
  children: PPComponent[];
  section: SectionConfiguration;
  show: boolean;
}> = ({ children, section, show }) => {
  const lstContext = useContext(ListViewContext);
  const stateCtx = useContext(StateContext);

  const formContext: UseFormReturn<FieldValues, any> = useFormContext();
  const apiRef: React.MutableRefObject<GridApiCommunity> = useGridApiRef();

  const fieldArray: Array<FieldConfiguration> = unwrapFieldConfiguration(
    children.map((comp) => {
      return comp.configuration;
    })
  );

  const setGridHeaders = (
    fieldConf: Array<FieldConfiguration>
  ): GridColDef[] => {
    let columns: GridColDef[] = [];
    let columnHeaderFields: FieldConfiguration[] = getGridHeaders(fieldConf);
    columnHeaderFields = columnHeaderFields.sort((a, b) => {
      if (
        a.extendedAttributes &&
        b.extendedAttributes &&
        a.extendedAttributes["gridHeaderOrder"] &&
        b.extendedAttributes["gridHeaderOrder"]
      )
        return (
          +a.extendedAttributes["gridHeaderOrder"] -
          +b.extendedAttributes["gridHeaderOrder"]
        );
      else return 0;
    });
    columnHeaderFields.forEach((fieldConf) =>
      columns.push({
        field: fieldConf.id,
        headerName: fieldConf.label,
        width: getTextWidth(fieldConf.label, undefined) + columnPadding,
      })
    );

    let deleteButton: ReactElement = section.extendedAttributes
      ?.gridDeleteButtonLabel ? (
      <Button size="small">
        {section.extendedAttributes.gridDeleteButtonLabel}
      </Button>
    ) : (
      <DeleteIcon />
    );

    let editButton: ReactElement = section.extendedAttributes
      ?.gridEditButtonLabel ? (
      <Button size="small">
        {section.extendedAttributes.gridEditButtonLabel}
      </Button>
    ) : (
      <EditIcon />
    );

    columns.push({
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 150,
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            key={"edit" + id}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={() => handleUpdateRow(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key={"delete" + id}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteRow(id)}
            color="inherit"
          />,
        ];
      },
    });
    return columns;

  };

  const headerFields: Array<FieldConfiguration> = getGridHeaders(fieldArray);

  const handleAddRow = () => {
    resetSectionFields();
    const newLstViewsCtx: ListViewsContextType = cloneDeep(
      lstContext.listViewsState
    );
    let newLstCtx: ListViewContextType | undefined = newLstViewsCtx[section.id];
    if (newLstCtx) {
      newLstCtx.expanded = !newLstCtx.expanded;
      if (!isEqual(lstContext.listViewsState, newLstViewsCtx))
        lstContext.setListViewsState(newLstViewsCtx);
    }
  };

  const handleSaveRow = () => {
    const appContext: AppContextType = cloneDeep(stateCtx.applicationContext);
    const listsContext: ListViewsContextType = cloneDeep(
      lstContext.listViewsState
    );
    const isSectionValid: boolean = validateInputForm(
      section,
      formContext.getValues(),
      appContext
    );
    if (!isEqual(stateCtx.applicationContext, appContext))
      stateCtx.setApplicationContext(appContext);
    if (isSectionValid) {
      const listRowModel: ListViewRowModel = {
        guid: "",
        //rowId: -1,
        dataModel: {},
        rowModel: {},
      };
      const submitListItemModel: ListViewSubmitDataModel = {
        guid: "",
        listItem: {
          id: undefined,
          uuid: undefined,
          name: "",
          sections: [],
        },
      };
      createUIRow(listRowModel, listsContext[section.id]);
      createRowDataModel(listRowModel);
      createSubmitDataModel(submitListItemModel, listRowModel);

      let uiModel = listsContext[section.id].dataModel.uiModel;
      uiModel = uiModel.filter((row) => row.guid !== listRowModel.guid);
      uiModel.push(listRowModel);

      let submitModel = listsContext[section.id].dataModel.submitRecordModel;
      submitModel = submitModel.filter(
        (row) => row.guid !== submitListItemModel.guid
      );
      submitModel.push(submitListItemModel);

      listsContext[section.id].dataModel = {
        uiModel: uiModel,
        submitRecordModel: submitModel,
      };
      listsContext[section.id].editRow = undefined;
      listsContext[section.id].expanded = !listsContext[section.id].expanded;

      validateListViewRequiredRows(section.id, listsContext);

      if (!isEqual(lstContext.listViewsState, listsContext))
        lstContext.setListViewsState(listsContext);

      //resetSectionFields();
    }
  };

  const createUIRow = (
    listRowModel: ListViewRowModel,
    currentListView: ListViewContextType
  ) => {
    const formFieldsData: FieldValues = formContext.getValues();
    //Obtain the guid whether it is an existing or new row

    const guid: string =
      currentListView.editRow !== undefined ? currentListView.editRow : v4();
    // Obtain the id of the current GridRowModel if the current row is been edited otherwise it uses the max number of rows to assign a new id
    // const rowId: number = currentListView?.editRow
    //   ? +currentListView.editRow.valueOf()
    //   : apiRef.current.getRowsCount();

    let newRow: GridValidRowModel = { id: guid };

    //Assign to the new row all values stored by react-hook-form that are within the headers defined for this list view.
    headerFields.forEach((field) => {
      newRow = {
        ...newRow,
        [field.id]:
          formFieldsData[field.id] instanceof Object
            ? formFieldsData[field.id].value
            : formFieldsData[field.id],
      };
    });

    //Call the method that updates the grid with thew row model.
    apiRef.current.updateRows([newRow]);

    listRowModel.guid = guid;
    // listRowModel.rowId = rowId;
    listRowModel.rowModel = newRow;
  };

  const createRowDataModel = (listRowModel: ListViewRowModel) => {
    const listViewSection: SectionConfiguration = section;
    // A list view must have a single form configured as a child element
    const listViewSectionForm: FormPropertiesConfiguration | undefined =
      listViewSection.forms !== undefined
        ? listViewSection.forms[0]
        : undefined;
    const dataModel: FieldValues = {};
    //There can only be one section under a single form for list view section type
    if (listViewSectionForm?.sections[0]) {
      const sectionConfiguration: SectionConfiguration =
        listViewSectionForm.sections[0];

      // Create a data model that represents all the data stored in react-hook-form for this row
      if (sectionConfiguration.fields) {
        sectionConfiguration.fields.forEach((fieldConf: FieldConfiguration) => {
          const value = formContext.getValues()[fieldConf.id];
          dataModel[fieldConf.id] = value;
        });
      }
    }
    listRowModel.dataModel = dataModel;
  };

  const createSubmitDataModel = (
    submitRecordModel: ListViewSubmitDataModel,
    listRowModel: ListViewRowModel
  ) => {
    const listViewSection: SectionConfiguration = section;
    // A list view must have a single form configured as a child element
    const listViewSectionForm: FormPropertiesConfiguration | undefined =
      listViewSection.forms !== undefined
        ? listViewSection.forms[0]
        : undefined;
    const submitRowModel: Form = {
      id: undefined,
      uuid: undefined,
      name: "",
      sections: [],
    };
    //There can only be one section under a single form for list view section type
    if (listViewSectionForm?.sections[0]) {
      const sectionConfiguration: SectionConfiguration =
        listViewSectionForm.sections[0];

      // Create a data model that represents all the data stored in react-hook-form for this row
      if (sectionConfiguration.fields) {
        const lstSection: Section = {
          id: sectionConfiguration.id,
          fields: [],
          type: sectionConfiguration.type,
        };
        const submitListViewItem: Form = {
          id: listViewSectionForm.id,
          uuid: listViewSectionForm.uuid,
          name: listViewSectionForm.name,
          sections: [lstSection],
        };
        sectionConfiguration.fields.forEach((fieldConf: FieldConfiguration) => {
          let value = formContext.getValues()[fieldConf.id];
          if (fieldConf.field_type === "phone") {
            value = peerplacePhoneFormat(value);
          }
          if (!isValueEmpty(value)) {
            const submitField: Field = {
              id: fieldConf.id,
              name: fieldConf.name,
              value: isTuple(value)
                ? [value.id]
                : isTupleArray(value)
                ? tupleArrayToStringArray(value)
                : [value],
            };
            lstSection.fields?.push(submitField);
          }
        });
        submitRecordModel.guid = listRowModel.guid;
        submitRecordModel.listItem = submitListViewItem;
      }
    }
  };

  const peerplacePhoneFormat = (input: string): string | undefined => {

    return input !== undefined
      ? input.slice(1, 4) + "-" + input.substring(6)
      : input;
  };

  const handleUpdateRow = (guid: GridRowId) => {

    const lstCtx: ListViewContextType =
      lstContext.listViewsState[section.id] ?? {};
    if (lstCtx.dataModel?.uiModel) {
      const rowValues: FieldValues = lstCtx.dataModel.uiModel.filter(
        (model) => model.guid === guid
      )[0].dataModel;
      if (rowValues) {
        for (const [key, value] of Object.entries(rowValues)) {
          formContext.setValue(key, value);
        }
      }
    }
    const newLstViewsCtx: ListViewsContextType = cloneDeep(
      lstContext.listViewsState
    );
    let newLstCtx: ListViewContextType | undefined = newLstViewsCtx[section.id];
    if (newLstCtx) {

      newLstCtx.editRow = guid as string;
      newLstCtx.expanded = !newLstCtx.expanded;
      newLstViewsCtx[section.id] = newLstCtx;
      if (!isEqual(lstContext.listViewsState, newLstViewsCtx)) {
        lstContext.setListViewsState(newLstViewsCtx);
      }
    }
  };

  const handleDeleteRow = (guid: GridRowId) => {
    const newLstViewsCtx: ListViewsContextType = cloneDeep(
      lstContext.listViewsState
    );

    if (newLstViewsCtx[section.id]) {
      newLstViewsCtx[section.id].dataModel.uiModel = newLstViewsCtx[
        section.id
      ].dataModel.uiModel.filter((row) => row.guid !== guid);
      newLstViewsCtx[section.id].dataModel.submitRecordModel = newLstViewsCtx[
        section.id
      ].dataModel.submitRecordModel.filter((row) => row.guid !== guid);

      if (!isEqual(lstContext.setListViewsState, newLstViewsCtx))
        lstContext.setListViewsState(newLstViewsCtx);
    }

    // validateListViewRequiredRows(
    //   section.id,
    //   lstContext.listViewsState,
    //   lstContext.setListViewsState
    // );

    apiRef.current.updateRows([{ id: [guid], _action: "delete" }]);
  };

  const isValueEmpty = (value: any): boolean => {
    if (isTuple(value) && (value.id == "" || value.id == undefined))
      return true;
    if (
      isTupleArray(value) &&
      value.filter((tuple: Tuple) => tuple.id != "").length == 0
    )
      return true;
    if (value === "" || value === undefined || value == null) return true;
    return false;
  };

  const handleCancelAddRow = () => {
    resetSectionFields();
    const newLstViewsCtx: ListViewsContextType = cloneDeep(
      lstContext.listViewsState
    );
    let newLstCtx: ListViewContextType | undefined = newLstViewsCtx[section.id];
    if (newLstCtx) {
      newLstCtx.expanded = !newLstCtx.expanded;
      newLstCtx.editRow = undefined;
      if (!isEqual(lstContext.listViewsState, newLstViewsCtx))
        lstContext.setListViewsState(newLstViewsCtx);
    }
    const newAppContext: AppContextType = cloneDeep(
      stateCtx.applicationContext
    );
    section.forms![0].sections[0].fields?.forEach(
      (field: FieldConfiguration) => (newAppContext.fieldState[field.id] = true)
    );
    stateCtx.setApplicationContext(newAppContext);
  };

  const resetSectionFields = (): void => {
    const sectionFieldArray: FieldConfiguration[] | undefined =
      section.forms![0].sections[0].fields;
    let newAppContext: AppContextType = cloneDeep(stateCtx.applicationContext);
    sectionFieldArray?.forEach((field) => {
      formContext.setValue(field.id, undefined);
      newAppContext.fieldHide[field.id] = false;
      newAppContext.fieldDisable[field.id] = false;
    });
    stateCtx.setApplicationContext(newAppContext);
  };

  const minimumNumberOfRequiredRows = (): number => {
    return section.extendedAttributes?.minimumListViewRows
      ? +section.extendedAttributes.minimumListViewRows
      : 0;
  };

  let addButtonLabel: string = section.extendedAttributes?.gridAddButtonLabel
    ? section.extendedAttributes.gridAddButtonLabel
    : "Add Record";

  const errorMessage: JSX.Element = (
    <Typography sx={{ color: red.A700 }}>
      {lstContext.listViewsState[section.id].errorState.message}
    </Typography>
  );

  return (
    <Card>
      <Box sx={{ ...style }}>
        {!lstContext.listViewsState[section.id]?.expanded ? (
          <Typography sx={{ color: red.A700 }}>
            Please Save/Cancel before navigating to the next screen
          </Typography>
        ) : null}
        <Collapse
          in={lstContext.listViewsState[section.id]?.expanded}
          timeout="auto"
        >
          <CardContent>
            <Box sx={{ ...style }}>
              {/* TODO: Refacto this to use HeaderMessage */}
              <Typography
                sx={{ fontWeight: "normal" }}
                color="#0f5da3"
                variant="h5"
                component="div"
              >
                {section.extendedAttributes?.listViewTitle}
                <Divider sx={{ my: 2 }} />
              </Typography>
              <Typography variant="body1">
                {section.extendedAttributes?.listViewDescription}
              </Typography>
            </Box>
            <Box sx={{ ...style }}>
              {!lstContext.listViewsState[section.id].errorState.state
                ? errorMessage
                : null}
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={handleAddRow}>
                  {addButtonLabel}
                </Button>
              </Stack>
              <DataGrid
                slots={{ pagination: customPagination }}
                sx={{ ...style }}
                apiRef={apiRef}
                rows={
                  lstContext.listViewsState[section.id]
                    ? [
                        ...lstContext.listViewsState[
                          section.id
                        ].dataModel.uiModel.map<GridValidRowModel>(
                          (row) => row.rowModel
                        ),
                      ]
                    : []
                }
                columns={setGridHeaders(fieldArray)}
              />
              {/* <PPDataGrid apiRef={apiRef}/> */}
            </Box>
          </CardContent>
        </Collapse>
        <Collapse
          in={!lstContext.listViewsState[section.id]?.expanded}
          timeout="auto"
          unmountOnExit
        >
          <CardContent>
            <Grid
              container
              spacing={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}
              columns={{ xs: 1, sm: 2, md: 4, lg: 4, xl: 4 }}
              justifyContent="flex-start"
            >
              {show &&
                children.map((comp) => {
                  return comp.node;
                })}
            </Grid>
            <Stack
              direction="row"
              justifyContent="right"
              padding={2}
              spacing={1}
            >
              <Button size="small" onClick={handleSaveRow}>
                Save
              </Button>
              <Button size="small" onClick={handleCancelAddRow}>
                Cancel
              </Button>
            </Stack>
          </CardContent>
        </Collapse>
      </Box>
    </Card>
  );
};

export default PPViewSection;
